import {
  catalogues,
  createCatalog,
  type Locale,
  type MessageKey,
  type MessageParamsOf,
  type ParameterizedKeyOf,
  type ParameterlessKeyOf,
  t as platformT,
} from '@croissants/i18n';
import { en, type GuestEnCatalogue } from './en';
import { fr } from './fr';

/**
 * The two catalogues this app reads from (PRD NFR-006).
 *
 *  - `guestCatalog` - screen copy owned by `order.` and nobody else. Built with
 *    the shared `createCatalog()` factory, so it gets the platform's typing,
 *    English fallback and completeness check without reimplementing any of it.
 *  - `platformText` - the strings that must read identically on every surface:
 *    the GST-036 stage model, price-breakdown labels, cancellation and tip copy.
 *    Those stay in `@croissants/i18n` and are never re-typed here.
 *
 * No component ever holds a literal string; both translators are reached through
 * `useTranslate()` in `src/state/locale.tsx`.
 */
export const guestCatalog = createCatalog({ en, fr });

export type GuestMessageKey = keyof GuestEnCatalogue & string;

/** Translator for this app's own screen copy. */
export type AppTranslate = {
  <K extends ParameterlessKeyOf<GuestEnCatalogue>>(key: K): string;
  <K extends ParameterizedKeyOf<GuestEnCatalogue>>(
    key: K,
    params: MessageParamsOf<GuestEnCatalogue, K>,
  ): string;
};

/**
 * Translator for the shared platform catalogue.
 *
 * `t` is overloaded so a parameterless key cannot be called with parameters and
 * vice versa. That is exactly right at a literal call site and unusable when the
 * key is computed - `STAGE_KEYS[stage].message` is a union of every key, and no
 * overload matches a union. This single widening, in one place, is what lets the
 * timeline resolve a key it looked up rather than one it hardcoded (GST-036).
 */
const widenedPlatformT = platformT as unknown as (
  locale: Locale,
  key: MessageKey,
  params?: Readonly<Record<string, string | number>>,
) => string;

export function platformTextFor(
  locale: Locale,
): (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string {
  return (key, params) => widenedPlatformT(locale, key, params);
}

export function appTextFor(locale: Locale): AppTranslate {
  return guestCatalog.translatorFor(locale);
}

/**
 * Narrows a catalogue key that arrived over the wire.
 *
 * The API answers every failure with a catalogue key so the guest reads it in
 * their own language, but a key the server knows and this build does not would
 * render as the raw key - `guest.not_servable` in the middle of a checkout. This
 * is the one place that can happen, so it is the one place that checks, falling
 * back to the generic message rather than showing a guest an identifier.
 */
export function platformMessageKey(raw: string): MessageKey {
  return raw in catalogues.en ? (raw as MessageKey) : 'error.unexpected';
}
